import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  createContext,
  useContext,
  PropsWithChildren,
} from 'react'
import { useNavigate } from 'react-router'
import ApiClient from '@quiqupltd/quiqupjs/lib/network/api-client'
import CaseNormaliser from '@quiqupltd/quiqupjs/lib/services/case-normaliser'
import { useEnvVars } from './useEnvVars'
import { useMutation } from 'react-query'
import useTrackingSocket from './useTrackingSocket'
import { Order } from '../types/Order'
import { externalStatus } from '../lib/status'
import {
  getOrderedStateChanges,
  getStateChangesWithDate,
  getUniqueStateChanges,
} from '../components/order-details/utils'
import { useAnalytics } from './useAnalytics'
import { notification } from 'antd'
import {
  RobotStatus,
  hasActiveRobotWidget,
  hasRobotLeg,
} from '../lib/robot-delivery'

const UUID_REGEX =
  /[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i

// The /order_management/tracking endpoint returns snake_case keys, but the whole
// app reads the order as camelCase (see the Order type + components, and the
// socket path which already camelises its payloads). Normalise the fetched order
// to camelCase so destination.trackingToken, stateChanges, serviceKind, etc. all
// resolve. Without this trackingToken is undefined → the tracking socket never
// connects → no live courier map (P1 go-live 2026-06-09: the ex-core-api CQRS
// tracking serializer returns snake_case where the old GraphQL path returned
// camelCase).
const camelNormaliser = new CaseNormaliser(CaseNormaliser.camelCase)

type GetOrderByIdParams = {
  orderId: string
  phone?: string
  dialingCode?: string
  replace?: boolean
  triggerAlert?: boolean
  // A silent re-fetch of the order already on screen (robot leg terminal
  // status): no analytics event, no navigation.
  refresh?: boolean
}
type GetOrderResponse = {
  order: Order
  trackingUrl: string
}
const getOrder =
  (url: string) =>
  async (params: GetOrderByIdParams): Promise<GetOrderResponse> => {
    const isUuid = UUID_REGEX.test(params.orderId)
    const countryCode = isUuid ? undefined : params.dialingCode
    const phoneNumber = isUuid ? undefined : params.phone
    try {
      const response = await ApiClient.get({
        path: url,
        includeAuth: false,
        params: {
          order_id: params.orderId,
          country_code: countryCode,
          phone_number: phoneNumber,
        },
      })

      if (params.dialingCode && params.phone && !response.order.uuid) {
        throw new Error('wrong phone number')
      }

      if (!response.trackingUrl && !response.order) {
        throw new Error('no tracking url')
      }

      return {
        order: response.order ? camelNormaliser.call(response.order) : response.order,
        trackingUrl: response.trackingUrl,
      }
    } catch (error) {
      throw error
    }
  }

type TrackingContextType = {
  isLoadingOrder: boolean
  isOrderError: boolean
  getOrder: (params: GetOrderByIdParams) => void
  orderDetails: Order | undefined
  invalidToken: boolean
  liveOrder: any
  mapCenter: any
  hasMap: boolean
  canLeaveAtDoor: boolean
  uniqueStateChanges: any
  // Nabd robot leg (sc-29236). `hasRobotLeg`: the order carries robot data at
  // all (hides Leave-at-door / Update-address). `hasWidget`: render Nabd's
  // widget instead of the courier map — a leg that is neither over nor expired.
  hasRobotLeg: boolean
  hasWidget: boolean
  // Latest `nabd.delivery.status` message from the widget, for the robot card.
  robotStatus: RobotStatus | undefined
  setRobotStatus: (status: RobotStatus) => void
  // Re-fetch the order with the same params as the last successful fetch
  // (keeps the phone-authenticated view). Used when the widget reports a
  // terminal status so the stepper moves from real state changes.
  refreshOrder: () => void
}

export const TrackingContext = createContext<TrackingContextType>({
  isLoadingOrder: false,
  isOrderError: false,
  getOrder: () => {},
  orderDetails: undefined,
  invalidToken: false,
  liveOrder: undefined,
  mapCenter: undefined,
  hasMap: false,
  canLeaveAtDoor: false,
  uniqueStateChanges: undefined,
  hasRobotLeg: false,
  hasWidget: false,
  robotStatus: undefined,
  setRobotStatus: () => {},
  refreshOrder: () => {},
})

export const useTracking = () => useContext(TrackingContext)

export const TrackingProvider = ({ children }: PropsWithChildren) => {
  const [orderDetails, setOrderDetails] = useState<Order | undefined>(undefined)
  const [robotStatus, setRobotStatus] = useState<RobotStatus | undefined>(
    undefined
  )
  // Params of the last successful fetch, replayed by refreshOrder. Without
  // this a refresh would fall back to the deep-link (no-phone) payload and
  // drop the destination, OTP and robot access code the customer unlocked.
  const lastParamsRef = useRef<GetOrderByIdParams | undefined>(undefined)
  const { env } = useEnvVars()
  const { track } = useAnalytics()
  const navigate = useNavigate()
  const [api, contextHolder] = notification.useNotification()
  const getOrderUrl = `${env.EXCORE_URL}/order_management/tracking`
  const {
    mutate: getOrderMutate,
    isLoading: isLoadingOrder,
    isError: isOrderError,
  } = useMutation({
    mutationFn: getOrder(getOrderUrl),
    onSuccess: (data: GetOrderResponse, callParams) => {
      lastParamsRef.current = callParams
      setOrderDetails(data.order)

      if (callParams.refresh) {
        return
      }

      // A genuinely new order (the provider lives at the app root and survives
      // "Track another order"): drop the previous widget's status so the robot
      // card cannot show the last order's floor/ETA until the new widget posts.
      // The silent terminal refresh above keeps it on purpose.
      setRobotStatus(undefined)

      track('Website Tracking Page Track Success', {
        action: 'redirect',
        category: 'tracking',
        product: 'TrackOrder',
        phone: callParams.phone,
        orderId: callParams.orderId,
      })

      if (data.trackingUrl) {
        navigate(data.trackingUrl, { replace: callParams.replace })

        return
      }

      navigate(`/${data.order.uuid ?? data.order.id}`, {
        replace: callParams.replace,
      })
    },
    onError: (error: any, callParams) => {
      if (callParams.triggerAlert !== undefined && !callParams.triggerAlert) {
        return
      }

      if (error.message === 'wrong phone number') {
        api.error({
          message: 'Wrong phone number',
          description:
            'Please use the phone number you used during order placement and try again.',
          duration: 10,
        })
        return
      }

      setOrderDetails(undefined)
      api.error({
        message: "We couldn't find your order",
        description: 'Please check the reference number and try again.',
        duration: 10,
      })
    },
  })
  const { invalidToken, liveOrder, mapCenter } = useTrackingSocket({
    token: orderDetails?.destination?.trackingToken,
    missionId: orderDetails?.mission?.id,
    shouldConnect: Boolean(
      !isLoadingOrder && !isOrderError && orderDetails?.id
    ),
  })

  const stateChangesWithDate = useMemo(
    () => getStateChangesWithDate(orderDetails?.stateChanges ?? []),
    [orderDetails?.stateChanges]
  )
  const stateChanges = useMemo(
    () => getOrderedStateChanges(stateChangesWithDate),
    [stateChangesWithDate]
  )
  const uniqueStateChanges = useMemo(
    () => getUniqueStateChanges(stateChanges),
    [stateChanges]
  )
  const hasToken = Boolean(orderDetails?.destination?.trackingToken)
  const hasMap =
    Boolean(
      hasToken &&
        !invalidToken &&
        ((orderDetails?.serviceKind !== 'partner_return' &&
          externalStatus[uniqueStateChanges[0]?.toState ?? ''] ===
            'Delivering') ||
          (orderDetails?.serviceKind === 'partner_return' &&
            ['out_for_collection', 'out_for_return'].includes(
              orderDetails?.state ?? ''
            )))
    ) &&
    orderDetails?.serviceKind !== 'partner_export' &&
    Boolean(liveOrder?.features?.length) &&
    Boolean(mapCenter)

  const canLeaveAtDoor = useMemo(
    () => {
      // Any requiredDocuments value that needs the customer at the door (OTP
      // code, in-person handover, ID photo) is incompatible with leave-at-door.
      // null/undefined/[] and unrelated values are fine.
      const blockingDocuments = [
        'otp',
        'in_person_delivery',
        'customer_identification_photo',
      ]
      const requiredDocuments = orderDetails?.requiredDocuments ?? []
      const hasBlockingDocument = requiredDocuments.some((doc) =>
        blockingDocuments.includes(doc)
      )

      return (
        !hasBlockingDocument &&
        [
          externalStatus.at_depot,
          externalStatus.collected,
          externalStatus.out_for_collection,
          externalStatus.out_for_delivery,
          externalStatus.pending,
          externalStatus.ready_for_collection,
          externalStatus.return_to_origin,
        ].includes(externalStatus[uniqueStateChanges[0]?.toState ?? 0])
      )
    },
    [orderDetails?.requiredDocuments, uniqueStateChanges]
  )

  const refreshOrder = useCallback(() => {
    const last = lastParamsRef.current
    if (!last) {
      return
    }
    getOrderMutate({ ...last, refresh: true, triggerAlert: false })
  }, [getOrderMutate])

  const trackingContextValue = {
    isLoadingOrder,
    isOrderError,
    getOrder: getOrderMutate,
    orderDetails,
    invalidToken,
    liveOrder,
    mapCenter,
    hasMap,
    canLeaveAtDoor,
    uniqueStateChanges,
    hasRobotLeg: hasRobotLeg(orderDetails),
    hasWidget: hasActiveRobotWidget(orderDetails),
    robotStatus,
    setRobotStatus,
    refreshOrder,
  }

  return (
    <TrackingContext.Provider value={trackingContextValue}>
      {children}
      {contextHolder}
    </TrackingContext.Provider>
  )
}
